import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from 'lucide-react';
interface HeroSectionProps {
  onNavigate: (page: 'shop') => void;
}
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const specials = [{
    id: 1,
    badge: 'Special of the Month',
    badgeColor: 'bg-red-100 text-red-700',
    accentColor: 'bg-red-500',
    bgColor: 'bg-red-50/50',
    headline: 'Premium nutrition, now at a premium discount!',
    discount: 'SAVE 20%',
    description: "on the entire Hill's Science Diet range",
    image: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?w=600&q=80',
    buttonColor: 'bg-brand-blue hover:bg-brand-blue-600 text-white'
  }, {
    id: 2,
    badge: 'Flea & Tick Season',
    badgeColor: 'bg-brand-blue-100 text-brand-blue-700',
    accentColor: 'bg-brand-blue',
    bgColor: 'bg-brand-blue-50/50',
    headline: 'Protect your pets from nasty parasites this season.',
    discount: 'SAVE 15%',
    description: 'on NexGard Spectra 6-packs',
    image: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=600&q=80',
    buttonColor: 'bg-brand-blue hover:bg-brand-blue-600 text-white'
  }, {
    id: 3,
    badge: 'Garden Essentials',
    badgeColor: 'bg-brand-green-100 text-brand-green-800',
    accentColor: 'bg-brand-green',
    bgColor: 'bg-brand-green-50/50',
    headline: 'Get your garden thriving with our premium fertilisers.',
    discount: 'BUY 2 GET 1',
    description: 'FREE on all Seasol liquid products',
    image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80',
    buttonColor: 'bg-brand-blue hover:bg-brand-blue-600 text-white'
  }];
  // Auto-cycle slides
  useEffect(() => {
    if (isHovering) return;
    const timer = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % specials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHovering, specials.length]);
  const nextSlide = () => setActiveSlide(prev => (prev + 1) % specials.length);
  const prevSlide = () => setActiveSlide(prev => (prev - 1 + specials.length) % specials.length);
  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };
  return <section className="bg-white pb-4 sm:pb-6" data-id="element-138">
      <div className="container-wide" data-id="element-139">
        <div className="relative rounded-b-2xl md:rounded-2xl overflow-hidden shadow-sm border border-gray-100 group min-h-[300px] sm:min-h-[280px] md:h-[260px]" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-140">
          {/* Slides Container */}
          <div className="relative h-full w-full" data-id="element-141">
            {specials.map((special, index) => <div key={special.id} className={`absolute inset-0 w-full h-full transition-opacity duration-500 ease-in-out flex flex-col md:flex-row ${index === activeSlide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'} ${special.bgColor}`} data-id="element-142">
                {/* Accent Bar (Left Edge) */}
                <div className={`hidden md:block absolute left-0 top-0 bottom-0 w-1.5 ${special.accentColor}`} data-id="element-143"></div>

                {/* Content Side (Left) */}
                <div className="flex-1 p-6 md:p-8 lg:p-10 flex flex-col justify-center md:pl-12" data-id="element-144">
                  <div className="mb-3" data-id="element-145">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${special.badgeColor}`} data-id="element-146">
                      {special.badge}
                    </span>
                  </div>

                  <h2 className="font-heading font-extrabold text-xl md:text-2xl lg:text-3xl text-gray-900 mb-2 leading-tight max-w-lg" data-id="element-147">
                    {special.headline}
                  </h2>

                  <div className="flex items-baseline gap-3 mb-2" data-id="element-148">
                    <span className="font-heading font-extrabold text-3xl md:text-4xl text-gray-900" data-id="element-149">
                      {special.discount}
                    </span>
                    <span className="text-sm md:text-base text-gray-600 font-medium" data-id="element-150">
                      {special.description}
                    </span>
                  </div>

                  <div className="mt-4 md:mt-6" data-id="element-151">
                    <button onClick={() => onNavigate('shop')} className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-heading font-bold text-sm md:text-base transition-colors shadow-sm active:scale-[0.98] ${special.buttonColor}`} data-id="element-152">
                      Shop Now <ArrowRightIcon className="w-4 h-4" data-id="element-153" />
                    </button>
                  </div>
                </div>

                {/* Image Side (Right) */}
                <div className="w-full md:w-2/5 lg:w-1/3 h-48 md:h-full relative overflow-hidden" data-id="element-154">
                  {/* Gradient mask for smooth blend on desktop */}
                  <div className="hidden md:block absolute inset-y-0 left-0 w-24 z-10" style={{
                background: `linear-gradient(to right, ${index === 0 ? 'rgba(254,242,242,0.8)' : index === 1 ? 'rgba(239,246,255,0.8)' : 'rgba(240,253,244,0.8)'}, transparent)`
              }} data-id="element-155"></div>
                  <img src={special.image} alt={special.headline} className="w-full h-full object-cover object-center" data-id="element-156" />
                </div>
              </div>)}
          </div>

          {/* Navigation Arrows (Desktop Hover) */}
          <button onClick={prevSlide} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md items-center justify-center text-gray-600 hover:text-brand-blue hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-20 active:scale-95" aria-label="Previous offer" data-id="element-157">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-158" />
          </button>
          <button onClick={nextSlide} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md items-center justify-center text-gray-600 hover:text-brand-blue hover:bg-white transition-all opacity-0 group-hover:opacity-100 z-20 active:scale-95" aria-label="Next offer" data-id="element-159">
            <ChevronRightIcon className="w-6 h-6" data-id="element-160" />
          </button>

          {/* Dot Indicators - Larger tap targets */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-20" data-id="element-161">
            {specials.map((_, index) => <button key={index} onClick={() => setActiveSlide(index)} className={`min-w-[44px] min-h-[44px] flex items-center justify-center transition-all duration-300 active:scale-95`} aria-label={`Go to slide ${index + 1}`} data-id="element-162">
                <div className={`rounded-full transition-all duration-300 ${index === activeSlide ? 'w-6 h-2 bg-gray-800' : 'w-2 h-2 bg-gray-400 hover:bg-gray-600'}`} data-id="element-163"></div>
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}